/**
 * Achievement rules are pure predicates over a compact history summary, so the
 * same code runs on device (instant feedback) and at the edge (leaderboards)
 * and always agrees. New rules are data, not code paths.
 */
export interface TastingSummary {
  readonly tastedAt: string; // ISO date-time
  readonly grape?: string;
  readonly country?: string;
  readonly region?: string;
  readonly blind: boolean;
  /** For blind tastings: did the user's grape call match the reveal? */
  readonly blindCorrect?: boolean;
}

export interface AchievementRule {
  readonly id: string;
  readonly title: string;
  readonly description: string;
  readonly check: (history: readonly TastingSummary[]) => boolean;
}

function distinct<T>(xs: readonly (T | undefined)[]): Set<T> {
  const s = new Set<T>();
  for (const x of xs) if (x !== undefined) s.add(x);
  return s;
}

/** Longest run of consecutive correct blind calls, in chronological order. */
export function longestBlindStreak(history: readonly TastingSummary[]): number {
  let best = 0;
  let run = 0;
  for (const t of history.toSorted((a, b) => a.tastedAt.localeCompare(b.tastedAt))) {
    if (!t.blind) continue;
    run = t.blindCorrect === true ? run + 1 : 0;
    best = Math.max(best, run);
  }
  return best;
}

export const ACHIEVEMENT_RULES: readonly AchievementRule[] = [
  {
    id: "first-note",
    title: "First sip",
    description: "Log your first structured tasting note.",
    check: (h) => h.length >= 1,
  },
  {
    id: "ten-grapes",
    title: "Ten grapes",
    description: "Taste ten different grape varieties.",
    check: (h) => distinct(h.map((t) => t.grape)).size >= 10,
  },
  {
    id: "five-countries",
    title: "Passport",
    description: "Taste wine from five countries.",
    check: (h) => distinct(h.map((t) => t.country)).size >= 5,
  },
  {
    id: "first-blind-hit",
    title: "Called it",
    description: "Name the grape correctly in a blind tasting.",
    check: (h) => h.some((t) => t.blind && t.blindCorrect === true),
  },
  {
    id: "blind-streak-3",
    title: "Hat-trick",
    description: "Three correct blind calls in a row.",
    check: (h) => longestBlindStreak(h) >= 3,
  },
];

/** Rules newly satisfied by `history` that are not yet in `unlocked`. */
export function evaluateAchievements(
  history: readonly TastingSummary[],
  unlocked: ReadonlySet<string>,
  rules: readonly AchievementRule[] = ACHIEVEMENT_RULES,
): AchievementRule[] {
  return rules.filter((r) => !unlocked.has(r.id) && r.check(history));
}

/**
 * One dated learning act. The kind is informational: the streak counts weeks,
 * never how many acts a week held or which sort they were.
 */
export interface LearningAct {
  readonly kind: "note" | "drill" | "lesson";
  /** ISO 8601 instant the act happened (a drill's answer time, not its sync time). */
  readonly at: string;
}

/** Consecutive ISO weeks ending in the week of `now` that are all in `weeks`. */
export function streakFromWeeks(weeks: ReadonlySet<string>, now: Date): number {
  let streak = 0;
  const cursor = new Date(now);
  while (weeks.has(isoWeekKey(cursor))) {
    streak += 1;
    cursor.setUTCDate(cursor.getUTCDate() - 7);
  }
  return streak;
}

/**
 * Consecutive ISO weeks (ending in the week of `now`) with at least one
 * learning act: a note (a tasting summary, solo or from a flight), a drill
 * answer or a completed lesson. Weekly, not daily: proef rewards learning, not
 * drinking frequency (AGENTS.md, product guardrails), so five drills or seven
 * days of practice are still one week.
 */
export function weeklyStreak(acts: readonly (TastingSummary | LearningAct)[], now: Date): number {
  const weeks = new Set(
    acts.map((act) => isoWeekKey(new Date("at" in act ? act.at : act.tastedAt))),
  );
  return streakFromWeeks(weeks, now);
}

/** `YYYY-Www` per ISO 8601 (weeks start Monday; week 1 contains the first Thursday). */
export function isoWeekKey(d: Date): string {
  const date = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const day = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - day);
  const yearStart = Date.UTC(date.getUTCFullYear(), 0, 1);
  const week = Math.ceil(((date.getTime() - yearStart) / 86_400_000 + 1) / 7);
  return `${date.getUTCFullYear()}-W${String(week).padStart(2, "0")}`;
}
